/**
 * SessionProgressContainer — 메뉴 화면 세션 진행/시트/결제 통합 (#603 [2][3]).
 *
 * 테이블 메뉴 화면(PickupMenu) 하단에 부착되어, 미결제 후불 주문(#654 — N건 가능)이
 * 있으면 진행바([2])를 노출하고, 행 탭으로 세션 시트([3])를 연다. "결제하기"/시트 CTA 는
 * #661 세션 결제 전용 페이지(/table-session/charge)로 정상 라우트 push 한다.
 *
 * 데이터·인증은 공유 훅 `useSessionUnpaidPostpay` 로 단일화(OrderHistoryPage 와 동일 출처).
 * 비테이블(픽업/뷰)에선 `enabled=false` 로 호출되어 조회·렌더 모두 생략된다(테이블 한정).
 *
 * ## 노출 트리거 (§0)
 *   `useSessionUnpaidPostpay().data !== null` (미결제 후불 주문서 존재). count≥2 아님.
 *
 * @see src/components/session/SessionProgressBar.tsx ([2])
 * @see src/components/session/SessionOrdersSheet.tsx ([3])
 * @see src/pages/SessionChargePage.tsx ([4] → #661 전용 페이지)
 * @see src/hooks/useSessionUnpaidPostpay.ts (공유 조회)
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { SessionProgressBar } from './SessionProgressBar';
import { SessionOrdersSheet } from './SessionOrdersSheet';
import { ComensalAccessPanel } from './ComensalAccessPanel';
import { hasPayableSessionLines, useSessionUnpaidPostpay } from '@/hooks/useSessionUnpaidPostpay';
import { useApp } from '@/contexts/AppContext';
import { useAuth } from '@/contexts/AuthContext';
import { useOrderHistory } from '@/contexts/OrderHistoryContext';
import { payNowTranslations } from '@/i18n/paymentModal';
import { ROUTES } from '@/config/routes';
import { getComensalSessionLabels } from '@/i18n/comensalSession';
import { useComensalSession } from '@/hooks/useComensalSession';
import {
  readComensalAccess,
  readSplitPaymentAvailable,
} from '@/lib/comensalAccess';
import { COMENSAL_ACCESS_CHANGED_EVENT } from '@/lib/sessionStorageKeys';

export interface SessionProgressContainerProps {
  /** 조회·렌더 활성 게이트 — 테이블 모드에서만 true (픽업/뷰는 false). */
  enabled: boolean;
  /** 장바구니에 담는 중 여부 — 진행바 2단/확장 분기. */
  cartHasItems: boolean;
  /** 테이블 번호 (시트 헤더 표시용). */
  tableNumber?: string | null;
  /** 실제 joined 세션 토큰. entry view-only 단계에서는 명시적 null. */
  sessionToken?: string | null;
  /** 활성 split 세션 QR이 발급한 짧은 view-only entry. */
  entryToken?: string | null;
  /**
   * 서버 권위 미결제 후불 존재 여부를 상위 UI 게이트(상단 TableOrderHeader PAY canPay)와 공유한다.
   * 진행바와 동일한 단일 조회(본 컴포넌트의 useSessionUnpaidPostpay 인스턴스)를 상향 보고로 재사용
   * — 부모가 별도 훅 인스턴스를 추가하면 중복 fetch + refetch 시 상태 분기가 생긴다.
   */
  onUnpaidPostpayChange?: (hasUnpaidPostpay: boolean) => void;
}

/**
 * 외부(상단 헤더 PAY 버튼 등)에서 세션 전체결제 페이지([4] → #661)로 보내는 imperative 핸들.
 * 하단 진행바 "결제하기"(handleChargeDirect)와 동일한 플로우로 합류시킨다 — 두 버튼이
 * 서로 다른 결제 플로우를 열면 안 된다 (같은 미결제 후불 = 같은 세션 결제 페이지).
 */
export interface SessionProgressContainerHandle {
  /** 세션 결제 페이지([4] → #661) 진입 — 미결제 후불 없으면 no-op (fail-closed). */
  openCharge: () => void;
}

export const SessionProgressContainer = forwardRef<
  SessionProgressContainerHandle,
  SessionProgressContainerProps
>(function SessionProgressContainer(
  {
    enabled,
    cartHasItems,
    tableNumber,
    sessionToken: sessionTokenProp,
    entryToken = null,
    onUnpaidPostpayChange,
  },
  ref,
) {
  const { locale, currency } = useApp();
  const { isLoggedIn } = useAuth();
  const { guestCredentials } = useOrderHistory();
  const navigate = useNavigate();

  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const guestPassword = isLoggedIn ? undefined : guestCredentials?.password;

  const participant = useComensalSession(
    enabled ? sessionTokenProp ?? null : null,
    enabled ? entryToken : null,
  );
  const [storedSplitAvailable, setStoredSplitAvailable] = useState(readSplitPaymentAvailable);
  const [storedAccess, setStoredAccess] = useState(readComensalAccess);

  useEffect(() => {
    const syncSplitPolicy = (): void => {
      setStoredSplitAvailable(readSplitPaymentAvailable());
      setStoredAccess(readComensalAccess());
    };
    window.addEventListener(COMENSAL_ACCESS_CHANGED_EVENT, syncSplitPolicy);
    return () => {
      window.removeEventListener(COMENSAL_ACCESS_CHANGED_EVENT, syncSplitPolicy);
    };
  }, []);

  const hasJoinedLedgerAccess = participant.comensal?.join_status === 'joined'
    && participant.comensal.session?.has_split_ledger === true;
  const hasStoredJoinedLedgerAccess = sessionTokenProp !== null
    && sessionTokenProp !== undefined
    && participant.deviceToken !== null
    && storedAccess?.sessionToken === sessionTokenProp
    && storedAccess.joinStatus === 'joined'
    && storedAccess.hasSplitLedger;
  const hasJoinedSplitAccess = participant.comensal?.join_status === 'joined'
    && (
      participant.comensal.session?.split_payment_available === true
      || hasJoinedLedgerAccess
    );
  const requiresSplitIdentity = storedSplitAvailable === true
    || participant.comensal?.session?.split_payment_available === true
    || hasJoinedLedgerAccess
    || hasStoredJoinedLedgerAccess;

  const {
    data,
    isLoading,
    sessionToken,
    splitPaymentAvailable,
  } = useSessionUnpaidPostpay({
    // split 모드의 무범위 quote는 host pay-all 전용이고, 개인 Bill 생성 뒤에는 대표도
    // 403이다. 메뉴 진행바는 identity CTA만 열고 exact scope 결정은 결제 페이지에 맡긴다.
    enabled: enabled && sessionTokenProp !== null && !requiresSplitIdentity,
    isLoggedIn,
    guestPassword: requiresSplitIdentity ? undefined : guestPassword,
  });
  const liveSessionToken = sessionTokenProp === undefined ? sessionToken : sessionTokenProp;

  // "미결제 후불 존재" = 조회 완료(!isLoading) && data 비-null (codex Important).
  // 훅은 count>0 && orders[0] 존재일 때만 data 를 세우지만(fail-closed — #635 scaffold 의
  // count/totalAmount 재파싱은 항상 참인 중복 검사라 단순화), refetch 중에는 직전 data 가
  // 남아 있어 stale true 로 상단 PAY 활성/openCharge 가 통과할 수 있으므로 isLoading 으로 차단.
  // #654 codex I3: 빈 items(계약 위반)면 시트 CTA 처럼 결제 진입도 차단 — 동일 판정 공유(SSOT).
  const hasUnpaidPostpay = !isLoading && hasPayableSessionLines(data);
  const hasChargeEntry = hasUnpaidPostpay || hasJoinedSplitAccess;

  useEffect(() => {
    onUnpaidPostpayChange?.(hasChargeEntry);
  }, [hasChargeEntry, onUnpaidPostpayChange]);

  // #661: 세션 결제 진입 = 전용 페이지로 정상 라우트 push (모달 히스토리 조작 없음 —
  // PR #622/#621 /cart 함정 회피). 뒤로가기 시 메뉴 화면으로 자연 복귀.
  // I3: 빈 items 계약 위반/조회 중 차단 — 배너/시트 CTA 와 동일 fail-closed(페이지도 재검사).
  const goToChargePage = useCallback(() => {
    if (isLoading || (!hasPayableSessionLines(data) && !hasJoinedSplitAccess)) return;
    navigate(ROUTES.TABLE_SESSION_CHARGE);
  }, [isLoading, data, hasJoinedSplitAccess, navigate]);

  // 상단 헤더 PAY 버튼 → 하단 진행바 "결제하기"와 동일한 [4] 직행 플로우 합류.
  // 조회 중/미결제 없음 → no-op (refetch 중 stale data 기반 진입 차단, fail-closed 보존).
  useImperativeHandle(
    ref,
    () => ({
      openCharge: goToChargePage,
    }),
    [goToChargePage],
  );

  const payNowT = payNowTranslations[locale];

  // 진행바/시트 라벨 (15언어 전수 — payNow.session 키).
  const progressLabels = useMemo(
    () => ({
      progressLabel: payNowT.session.progressLabel,
      payButton: payNowT.session.payButton,
    }),
    [payNowT.session.progressLabel, payNowT.session.payButton],
  );

  const sheetLabels = useMemo(
    () => ({
      title: payNowT.session.sheetTitle,
      totalLabel: payNowT.session.sheetTotalLabel,
      payButton: payNowT.session.sheetPayButton,
      empty: payNowT.session.sheetEmpty,
      closeLabel: payNowT.session.sheetClose,
    }),
    [payNowT.session],
  );

  const handleOpenSheet = useCallback(() => setIsSheetOpen(true), []);
  const handleCloseSheet = useCallback(() => setIsSheetOpen(false), []);

  // 진행바 결제 CTA → 시트 거치지 않고 [4] 페이지 직행.
  const handleChargeDirect = goToChargePage;

  // 시트 "전체 결제하기" → 시트 닫고 [4] 페이지 진입.
  const handleChargeFromSheet = useCallback(() => {
    setIsSheetOpen(false);
    goToChargePage();
  }, [goToChargePage]);

  const showAccessBoundary = entryToken !== null
    || participant.deviceToken !== null
    || (storedSplitAvailable === true && liveSessionToken !== null)
    || (splitPaymentAvailable === true && liveSessionToken !== null)
    || (hasStoredJoinedLedgerAccess && liveSessionToken !== null);

  // 미결제 주문과 합류 경계가 모두 없으면 미렌더한다.
  if (!data && !showAccessBoundary) return null;

  return (
    <>
      {showAccessBoundary ? (
        <ComensalAccessPanel
          sessionToken={liveSessionToken ?? null}
          entryToken={entryToken}
          isAvailable
          labels={getComensalSessionLabels(locale)}
          state={participant}
        />
      ) : null}

      {hasJoinedSplitAccess ? (
        <button
          type="button"
          onClick={goToChargePage}
          className="w-full border-t border-border bg-background px-4 py-3 font-semibold text-primary"
          data-testid="split-charge-entry"
        >
          {hasJoinedLedgerAccess && participant.comensal?.session?.split_payment_available === false
            ? locale === 'es' ? 'Ver cuentas' : locale === 'ko' ? '계산서 보기' : 'View bills'
            : locale === 'es' ? 'Dividir y pagar' : locale === 'ko' ? '나누고 결제하기' : 'Split and pay'}
        </button>
      ) : null}

      {data !== null ? (
        <SessionProgressBar
          itemCount={data.items.length}
          totalAmount={data.totalAmount}
          currency={currency}
          cartHasItems={cartHasItems}
          labels={progressLabels}
          onOpenSheet={handleOpenSheet}
          onCharge={handleChargeDirect}
        />
      ) : null}

      {/* 시트는 document.body 로 portal — 진행바는 PickupMenu 하단 도크(sticky, viewport
          바닥 고정) 안에 렌더되는데, position: sticky 는 stacking context 를 생성하므로 시트가
          도크 내부에 남으면 z-55 가 도크(z-auto) 안에 갇혀 하단 네비(z-50)·CartModal(z-50)
          아래로 깔리는 #634 역전이 재발한다. portal 로 루트 stacking context 에 둬 계층을 보존.
          (#661: 결제 모달은 전용 페이지로 전환되어 portal 대상에서 제외) */}
      {data !== null ? createPortal(
        <SessionOrdersSheet
          isOpen={isSheetOpen}
          orders={data.orders}
          totalAmount={data.totalAmount}
          tableNumber={tableNumber}
          currency={currency}
          labels={sheetLabels}
          onClose={handleCloseSheet}
          onCharge={handleChargeFromSheet}
        />,
        document.body,
      ) : null}

    </>
  );
});

export default SessionProgressContainer;
